import { useMemo, useState } from 'react';
import { Settings } from 'lucide-react';
import { useTranslation } from '@/lib/i18n';
import { Badge, Button, EmptyState, SectionCard, Skeleton, StatusDot, Switch, toast } from '@/components/ui';
import { useHasRole } from '@/providers/AuthProvider';
import { NOTIFICATION_INTEGRATION_WRITE_ROLES } from '../lib/notificationActionRbac';
import { triggerLabel } from '../lib/notificationLabels';
import {
  useNotificationTriggersQuery,
  useUpdateNotificationTriggerMutation,
} from '../hooks/useNotificationTriggersQuery';
import { useTriggerCatalogQuery } from '../hooks/useAutoNotificationQuery';
import type { NotificationTrigger } from '../types/notificationTrigger';
import type { TriggerCatalogItem } from '../types/notificationRule';
import { NotificationSettingsModal } from '../components/NotificationSettingsModal';

const ALL = '__all__';

/**
 * 자동 알림 탭 — 카테고리 패널(좌) + 트리거 규칙 리스트(우) + 설정 모달. (디자인 스펙 §3)
 *
 * 데이터: 트리거 ON/OFF 목록(useNotificationTriggersQuery, plural triggers) + 카탈로그
 * (카테고리/변수/wired) 합성. 행 토글=trigger PUT, 상세 설정=설정 모달이 rule GET/PUT.
 */
export const AutoNotificationTab = () => {
  const { t } = useTranslation('notifications');
  const canWrite = useHasRole(NOTIFICATION_INTEGRATION_WRITE_ROLES);

  const triggersQuery = useNotificationTriggersQuery();
  const catalogQuery = useTriggerCatalogQuery();
  const updateTrigger = useUpdateNotificationTriggerMutation();

  const [activeCategory, setActiveCategory] = useState<string>(ALL);
  const [settingsCode, setSettingsCode] = useState<string | null>(null);

  const catalogByCode = useMemo(() => {
    const map = new Map<string, TriggerCatalogItem>();
    (catalogQuery.data ?? []).forEach((c) => map.set(c.triggerCode, c));
    return map;
  }, [catalogQuery.data]);

  const triggers = useMemo(() => triggersQuery.data ?? [], [triggersQuery.data]);

  const categoryLabelOf = (code: string): string => catalogByCode.get(code)?.categoryLabel ?? t('auto.category.etc', '기타');
  const categoryCodeOf = (code: string): string => catalogByCode.get(code)?.category ?? 'etc';
  // 라벨 SSoT: BE 카탈로그 label 1순위 → 트리거 응답 label → i18n trigger.<CODE> 폴백 → raw code.
  // (i18n 폴백은 카탈로그가 라벨을 못 준 신규/미연동 트리거에 raw 코드 노출을 막는다 — ID-226.)
  const labelOf = (trigger: NotificationTrigger): string =>
    catalogByCode.get(trigger.triggerCode)?.label ??
    trigger.label ??
    triggerLabel(t, trigger.triggerCode) ??
    trigger.triggerCode;

  // 카테고리별 그룹(카탈로그 categoryLabel 기준, 없으면 '기타').
  const categories = useMemo(() => {
    const byCode = new Map<string, { code: string; label: string; count: number }>();
    triggers.forEach((tr) => {
      const code = categoryCodeOf(tr.triggerCode);
      const label = categoryLabelOf(tr.triggerCode);
      const entry = byCode.get(code) ?? { code, label, count: 0 };
      entry.count += 1;
      byCode.set(code, entry);
    });
    return Array.from(byCode.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers, catalogByCode, t]);

  const filteredTriggers = useMemo(() => {
    if (activeCategory === ALL) return triggers;
    return triggers.filter((tr) => categoryCodeOf(tr.triggerCode) === activeCategory);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggers, activeCategory, catalogByCode]);

  const toggleEnabled = (trigger: NotificationTrigger, next: boolean) => {
    updateTrigger.mutate(
      { triggerCode: trigger.triggerCode, body: { enabled: next, channels: trigger.channels } },
      {
        onSuccess: () =>
          toast.success(
            next ? t('auto.toast.enabled', '자동 알림이 켜졌습니다.') : t('auto.toast.disabled', '자동 알림이 꺼졌습니다.'),
          ),
        onError: (e) => toast.error(e.message ?? t('auto.toast.saveFail', '자동 알림 설정에 실패했습니다.')),
      },
    );
  };

  const channelBadge = (trigger: NotificationTrigger) => {
    const hasKakao = trigger.channels.includes('KAKAO_BIZ');
    const hasSms = trigger.channels.includes('SMS');
    if (hasKakao && hasSms) {
      return <Badge variant="success" size="sm">{`${t('channel.KAKAO_BIZ')} · ${t('channel.SMS')}`}</Badge>;
    }
    if (hasKakao) return <Badge variant="info" size="sm">{t('channel.KAKAO_BIZ')}</Badge>;
    if (hasSms) return <Badge variant="warn" size="sm">{t('channel.SMS')}</Badge>;
    if (trigger.channels.includes('EMAIL')) return <Badge variant="neutral" size="sm">{t('channel.EMAIL')}</Badge>;
    return null;
  };

  const isPending = triggersQuery.isPending || catalogQuery.isPending;
  const isError = triggersQuery.isError;

  if (isPending) {
    return (
      <div className="space-y-2" role="status" aria-live="polite" data-testid="auto-notification-tab">
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-[56px] w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="auto-notification-tab">
        <EmptyState
          title={t('auto.error', '자동 알림 목록을 불러오지 못했습니다.')}
          action={
            <Button variant="outline" size="sm" onClick={() => { void triggersQuery.refetch(); void catalogQuery.refetch(); }}>
              {t('common.retry', '다시 시도')}
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 lg:flex-row" data-testid="auto-notification-tab">
      {/* 좌 — 카테고리 패널 */}
      <div className="w-full lg:w-[220px] lg:shrink-0">
        <SectionCard padded={false}>
          <div className="flex flex-col py-1">
            <CategoryRow
              label={t('common.all', '전체')}
              count={triggers.length}
              active={activeCategory === ALL}
              onClick={() => setActiveCategory(ALL)}
            />
            {categories.map((c) => (
              <CategoryRow
                key={c.code}
                label={c.label}
                count={c.count}
                active={activeCategory === c.code}
                onClick={() => setActiveCategory(c.code)}
              />
            ))}
          </div>
        </SectionCard>
      </div>

      {/* 우 — 규칙 리스트 */}
      <div className="min-w-0 flex-1">
        {filteredTriggers.length === 0 ? (
          <EmptyState title={t('auto.empty', '등록된 자동 알림이 없습니다.')} />
        ) : (
          <SectionCard
            title={activeCategory === ALL ? t('common.all', '전체') : categoryLabelOf(filteredTriggers[0]?.triggerCode ?? '')}
            aside={<Badge variant="neutral" size="sm">{filteredTriggers.length}</Badge>}
          >
            <div className="flex flex-col">
              {filteredTriggers.map((trigger, idx) => {
                const catalog = catalogByCode.get(trigger.triggerCode);
                const wired = catalog?.wired ?? true;
                return (
                  <div
                    key={trigger.triggerCode}
                    className="flex flex-col gap-2 py-3"
                    style={idx > 0 ? { borderTop: '1px solid var(--divider-soft)' } : undefined}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[14px] font-semibold" style={{ color: 'var(--content-strong)' }}>
                          {labelOf(trigger)}
                        </span>
                        {channelBadge(trigger)}
                        {!wired ? (
                          <span className="inline-flex items-center gap-1">
                            <StatusDot tone="neutral" label={t('auto.unwired', '미연동')} />
                            <Badge variant="neutral" size="sm">{t('auto.unwired', '미연동')}</Badge>
                          </span>
                        ) : null}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!canWrite}
                        onClick={() => setSettingsCode(trigger.triggerCode)}
                      >
                        <Settings size={14} aria-hidden="true" />
                        {t('common.edit', '설정')}
                      </Button>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-[13px]" style={{ color: 'var(--content-muted)' }}>
                        {t('auto.settings.enabled', '자동 알림 사용')}
                      </span>
                      <Switch
                        checked={trigger.enabled}
                        disabled={
                          !canWrite ||
                          (updateTrigger.isPending && updateTrigger.variables?.triggerCode === trigger.triggerCode)
                        }
                        onCheckedChange={(v) => toggleEnabled(trigger, v)}
                        aria-label={`${labelOf(trigger)} ${t('auto.settings.enabled', '자동 알림 사용')}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </SectionCard>
        )}
      </div>

      <NotificationSettingsModal
        triggerCode={settingsCode}
        catalog={settingsCode ? (catalogByCode.get(settingsCode) ?? null) : null}
        onClose={() => setSettingsCode(null)}
      />
    </div>
  );
};

interface CategoryRowProps {
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}

const CategoryRow = ({ label, count, active, onClick }: CategoryRowProps) => (
  <button
    type="button"
    onClick={onClick}
    className="relative flex items-center justify-between px-4 py-2.5 text-left text-[13px] transition-colors"
    style={
      active
        ? { background: 'var(--accent-soft)', color: 'var(--accent-default)', fontWeight: 600 }
        : { color: 'var(--content-default)' }
    }
  >
    {active ? (
      <span
        aria-hidden="true"
        className="absolute left-0 top-1/2 h-5 w-[2px] -translate-y-1/2"
        style={{ background: 'var(--accent-default)' }}
      />
    ) : null}
    <span>{label}</span>
    <Badge variant="neutral" size="sm">{count}</Badge>
  </button>
);
